import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, CircularProgress, Dialog, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import PageSearchBar from '../../common/PageSearchBar';
import CustomButton from '../../common/CustomButton';
import './BetaReviews.scss';

const STATUS_TABS = [
  { key: 'in_review', label: 'In review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

const STATUS_LABELS = Object.fromEntries(STATUS_TABS.map((t) => [t.key, t.label]));

const SECTION_LABELS = { day_one: 'Day One', two_weeks: 'Two Weeks', bugs_feedback: 'Bugs & Feedback' };

const TASK_LABELS = {
  beta_sms_backup_test: 'Test the SMS backup',
  beta_first_impressions: 'First impressions',
  beta_report_bug: 'Report a bug or confusion',
  beta_weekly_checkin: 'Weekly check-in',
  beta_updated_feedback: 'Updated feedback',
  beta_video_walkthrough: 'Video walkthrough',
  beta_critical_bug: 'Critical bug',
  beta_major_issue: 'Major issue',
  beta_minor_issue: 'Minor issue',
  beta_feature_suggestion: 'Feature suggestion',
};

const taskLabel = (rewardType) => TASK_LABELS[rewardType] || rewardType.replace(/^beta_/, '').replace(/_/g, ' ');

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '');

const plural = (count, word) => (count ? `${count} ${word}${count > 1 ? 's' : ''}` : null);

const proofSummary = (s) =>
  [
    plural(s.files.filter((f) => f.kind === 'image').length, 'image'),
    plural(s.files.filter((f) => f.kind === 'video').length, 'video'),
    plural(s.links.length, 'link'),
    s.text && 'text',
  ].filter(Boolean).join(' · ');

export default function BetaReviews() {
  const authCtx = useContext(AuthContext);
  const [status, setStatus] = useState('in_review');
  const [rewardType, setRewardType] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [counts, setCounts] = useState({});
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [noteMissing, setNoteMissing] = useState(false);
  const noteRef = useRef(null);

  const getSubmissions = () => {
    setLoading(true);
    const params = new URLSearchParams({ status });
    if (rewardType) params.set('rewardType', rewardType);
    fetch(`${process.env.REACT_APP_API_URI}/v3/admin/beta/submissions?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authCtx.token }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 'success') {
          setSubmissions(data.data || []);
          setCounts(data.counts || {});
          setTasks(data.tasks || []);
        } else {
          toast.error(data.message || 'Failed to load submissions.');
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load submissions.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getSubmissions();
  }, [status, rewardType]);

  const displayed = useMemo(() => {
    const term = searchInput.trim().toLowerCase();
    if (!term) return submissions;
    return submissions.filter((s) =>
      [s.user.name, s.user.email, s.text, taskLabel(s.rewardType)].some((v) => (v || '').toLowerCase().includes(term))
    );
  }, [submissions, searchInput]);

  const closeReview = () => {
    if (saving) return;
    setSelected(null);
    setNote('');
    setReviewError(null);
    setNoteMissing(false);
  };

  const review = async (action) => {
    setReviewError(null);
    if (action === 'reject' && !note.trim()) {
      setNoteMissing(true);
      noteRef.current?.focus();
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URI}/v3/admin/beta/submissions/${selected.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authCtx.token },
        body: JSON.stringify({ action, note: note.trim() })
      });
      const data = await response.json();
      if (data.status !== 'success') {
        setReviewError(data.message || 'Review failed. Please try again.');
        return;
      }
      if (action === 'reject') toast.success('Submission rejected.');
      else if (data.credited > 0) toast.success(`Approved. ${data.credited} points awarded.`);
      else toast.success('Approved. Points are awarded once the task count is full.');
      setSelected(null);
      setNote('');
      getSubmissions();
    } catch (err) {
      console.error(err);
      setReviewError('Could not reach the server. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tabCount = (key) =>
    key === 'all' ? Object.values(counts).reduce((sum, n) => sum + n, 0) : counts[key] || 0;

  return (
    <>
      <div className="beta-reviews-container">
        <div className="header-section">
          <span className="title">BETA REVIEWS</span>
          <div className="header-items-div">
            <PageSearchBar value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search tester or text" />
            <Select size="small" value={rewardType} displayEmpty onChange={(e) => setRewardType(e.target.value)} className="task-select">
              <MenuItem value="">All tasks</MenuItem>
              {tasks.map((t) => (
                <MenuItem key={t.rewardType} value={t.rewardType}>{taskLabel(t.rewardType)}</MenuItem>
              ))}
            </Select>
            <div className="status-btns">
              {STATUS_TABS.map((tab) => (
                <div key={tab.key} className={`filter-item ${status === tab.key ? 'selected' : ''}`} onClick={() => setStatus(tab.key)}>
                  {tab.label}({tabCount(tab.key)})
                </div>
              ))}
            </div>
          </div>
        </div>

        <TableContainer component={Paper} className="beta-table" sx={{ height: '100%', overflow: 'auto' }}>
          <Table sx={{ minWidth: 700, tableLayout: 'fixed' }} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className="head-cell" style={{ width: '170px' }}>Submitted</TableCell>
                <TableCell className="head-cell" style={{ width: '220px' }}>Tester</TableCell>
                <TableCell className="head-cell" style={{ width: '230px' }}>Task</TableCell>
                <TableCell className="head-cell">Proof</TableCell>
                <TableCell className="head-cell" style={{ width: '130px' }}>Status</TableCell>
                <TableCell className="head-cell" style={{ width: '110px' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ border: '0px' }}>
                    <CircularProgress size={52} style={{ color: '#e84118' }} />
                  </TableCell>
                </TableRow>
              ) : displayed.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" className="empty-cell">Nothing here.</TableCell>
                </TableRow>
              ) : (
                displayed.map((s) => (
                  <TableRow key={s.id} className="beta-row" onClick={() => setSelected(s)}>
                    <TableCell className="body-cell">{formatDate(s.createdAt)}</TableCell>
                    <TableCell className="body-cell">
                      <div className="primary-text">{s.user.name || s.user.id}</div>
                      <div className="muted">{s.user.email}</div>
                    </TableCell>
                    <TableCell className="body-cell">
                      <div className="primary-text">{taskLabel(s.rewardType)}</div>
                      <div className="muted">{SECTION_LABELS[s.section] || s.section} · {s.points} pts{s.max > 1 ? ` for ${s.max}` : ''}</div>
                    </TableCell>
                    <TableCell className="body-cell">
                      <div className="proof-summary">{proofSummary(s)}</div>
                      {s.text ? <div className="muted text-snippet">{s.text}</div> : null}
                    </TableCell>
                    <TableCell className="body-cell">
                      <div className={`beta-status-${s.status}`}>{STATUS_LABELS[s.status] || s.status}</div>
                    </TableCell>
                    <TableCell className="body-cell" align="right">
                      <CustomButton className={s.status === 'in_review' ? 'row-btn' : 'deliva-btn-bordered row-btn'}>
                        {s.status === 'in_review' ? 'Review' : 'View'}
                      </CustomButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <Dialog open={!!selected} onClose={closeReview} maxWidth="md" fullWidth>
        {selected ? (
          <div className="modal-container beta-review-modal">
            <div className="modal-header">
              <h1>{taskLabel(selected.rewardType)}</h1>
              <span className="modal-subtitle">
                {SECTION_LABELS[selected.section] || selected.section} · {selected.points} pts{selected.max > 1 ? ` once ${selected.max} are approved` : ''}
              </span>
            </div>

            <div className="modal-body">
              {reviewError ? (
                <Alert severity="error" onClose={() => setReviewError(null)}>{reviewError}</Alert>
              ) : null}

              <div className="dialog-row">
                <div>
                  <div className="label">Tester</div>
                  <div className="primary-text">{selected.user.name || selected.user.id}</div>
                  <div className="muted">{selected.user.email}</div>
                </div>
                <div>
                  <div className="label">Submitted</div>
                  <div>{formatDate(selected.createdAt)}</div>
                </div>
                <div>
                  <div className="label">Status</div>
                  <div className={`beta-status-${selected.status}`}>{STATUS_LABELS[selected.status] || selected.status}</div>
                </div>
              </div>

              {selected.text ? (
                <div>
                  <div className="label">Description</div>
                  <div className="submission-text">{selected.text}</div>
                </div>
              ) : null}

              {selected.files.length ? (
                <div>
                  <div className="label">Files</div>
                  <div className="media-grid">
                    {selected.files.map((file) =>
                      file.kind === 'video' ? (
                        <video key={file.url} src={file.url} controls preload="metadata" className="media-video" />
                      ) : (
                        <a key={file.url} href={file.url} target="_blank" rel="noopener noreferrer">
                          <img src={file.url} alt="Submitted screenshot" className="media-image" />
                        </a>
                      )
                    )}
                  </div>
                </div>
              ) : null}

              {selected.links.length ? (
                <div>
                  <div className="label">Links</div>
                  {selected.links.map((link) => (
                    <div key={link}>
                      <a href={link} target="_blank" rel="noopener noreferrer" className="submission-link">{link}</a>
                    </div>
                  ))}
                </div>
              ) : null}

              {selected.status === 'in_review' ? (
                <TextField
                  label="Note to the tester (required to reject)"
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    setNoteMissing(false);
                  }}
                  inputRef={noteRef}
                  error={noteMissing}
                  helperText={noteMissing ? 'Add a note so the tester knows why it was rejected.' : null}
                  multiline
                  minRows={2}
                  fullWidth
                  inputProps={{ maxLength: 1000 }}
                />
              ) : (
                <div>
                  <div className="label">Review</div>
                  <div>{selected.reviewedBy ? `${STATUS_LABELS[selected.status]} by ${selected.reviewedBy}` : STATUS_LABELS[selected.status]} · {formatDate(selected.reviewedAt)}</div>
                  {selected.reviewNote ? <div className="submission-text">{selected.reviewNote}</div> : null}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <Button className="cancel-btn" onClick={closeReview} disabled={saving}>Close</Button>
              {selected.status === 'in_review' ? (
                <div className="footer-actions">
                  <Button className="reject-btn" onClick={() => review('reject')} disabled={saving}>Reject</Button>
                  <Button onClick={() => review('approve')} disabled={saving}>
                    {saving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : 'Approve'}
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </Dialog>
    </>
  );
}
